import { DatePicker } from "@totalenergiescode/mobility-business-rev-design-system";
import {
  Suffix,
  translateField,
  useFormDataContext,
} from "@totalenergiescode/mobility-business-rev-form-data/components";
import dayjs from "dayjs";

import { StyledDatePicker } from "../DatePickerField/styles";

interface DateRangePickerFieldProps {
  fieldId: string;
}

function DateRangePickerField({ fieldId }: DateRangePickerFieldProps) {
  const {
    dataTestIdPrefix,
    findFieldById,
    findCompositeFieldByName,
    translatorFor,
  } = useFormDataContext();

  const startField = findFieldById(fieldId);
  const endField = findCompositeFieldByName(startField, "endDate");

  const startTranslator = translatorFor(startField);
  const startLabel = translateField(startTranslator, startField, Suffix.LABEL);

  const endTranslator = translatorFor(endField);
  const endLabel = translateField(endTranslator, endField, Suffix.LABEL);

  const startValue = startField.value.asString();
  const currentStart = startValue.value() || startValue.defaultValue();
  const startDateValue = currentStart
    ? dayjs(currentStart, "YYYY-MM-DD")
    : undefined;

  const endValue = endField.value.asString();
  const currentEnd = endValue.value() || endValue.defaultValue();
  const endDateValue = currentEnd ? dayjs(currentEnd, "YYYY-MM-DD") : undefined;

  function isNewStartAfterCurrentEnd(newValue: dayjs.Dayjs | null) {
    return newValue?.isAfter(endDateValue);
  }

  function isNewEndBeforeCurrentStart(newValue: dayjs.Dayjs | null) {
    return newValue?.isBefore(startDateValue);
  }

  function handleStartDateChange(newValue: dayjs.Dayjs | null) {
    const newDate = newValue?.format("YYYY-MM-DD") || "";
    startField.setValue(newDate);

    if (isNewStartAfterCurrentEnd(newValue)) {
      endField.setValue("");
    }
  }

  function handleEndDateChange(newValue: dayjs.Dayjs | null) {
    const newDate = newValue?.format("YYYY-MM-DD") || "";
    endField.setValue(newDate);

    if (isNewEndBeforeCurrentStart(newValue)) {
      startField.setValue("");
    }
  }

  return (
    <div>
      <StyledDatePicker>
        <DatePicker
          clearable={true}
          dataTestId={`${dataTestIdPrefix}-${startField.id()}`}
          label={startLabel.toString()}
          isDisabled={!startField.writable}
          maxDate={endDateValue}
          onChange={handleStartDateChange}
          value={startDateValue}
        />
      </StyledDatePicker>

      <StyledDatePicker>
        <DatePicker
          clearable={true}
          dataTestId={`${dataTestIdPrefix}-${endField.id()}`}
          label={endLabel.toString()}
          isDisabled={!endField.writable}
          minDate={startDateValue}
          onChange={handleEndDateChange}
          value={endDateValue}
        />
      </StyledDatePicker>
    </div>
  );
}

export default DateRangePickerField;
